import { useApiError } from "@raiden/library/components/ReactHookForm/ApiError";
import {
  PAYMENTS_METHODS_VALUE_CREDIT_CARD_PAYBOX,
  PAYMENTS_STATES_VALUE_PAID,
  PAYMENTS_TYPES_VALUE_PAYMENT,
} from "@raiden/library/constants/payments";
import { initString } from "@raiden/library/helpers/form/initString";
import getPaymentsMethodsForType from "@raiden/library/helpers/payments/getPaymentsMethodsForType";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import normalizePaymentCreate from "@raiden/library/normalizers/payments/normalizeCreate";
import { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { PAYMENTS_CREATE_FORM_ID } from "./FormPayment";

/**
 * @typedef {object} CreditCardSettings
 * @property {Object<string, string>} inputs
 * @property {object} settings
 * @property {string} settings.url
 */

/**
 * @typedef {object} InvoiceData
 * @property {string} invoice_id
 * @property {string} amount
 * @property {boolean} isSelected
 */

/**
 * @typedef {object} FormValues
 * @property {object} data
 * @property {string} data.environment_id
 * @property {string} data.type
 * @property {string} data.state
 * @property {string} data.customer_id
 * @property {string} data.method
 * @property {InvoiceData[]} data.invoices
 */

/**
 * @param {object} params
 * @param {number} params.environmentId
 * @param {number} params.customerId
 * @param {import("@raiden/library/types/Payment").PaymentMethod} [params.method]
 * @param {InvoiceData[]} [params.invoices]
 *
 * @returns {FormValues}
 */
function getDefaultValues({
  environmentId,
  customerId,
  method,
  invoices = [],
}) {
  return {
    data: {
      environment_id: String(environmentId),
      type: PAYMENTS_TYPES_VALUE_PAYMENT,
      state: PAYMENTS_STATES_VALUE_PAID,
      customer_id: String(customerId),
      method: method ?? "",
      invoices: [...invoices],
    },
  };
}

/**
 * @typedef {object} Props
 * @property {number} environmentId
 * @property {number} customerId
 * @property {import("@raiden/library/types/Invoice").Invoice[]} invoices
 * @property {number[]} [initialInvoiceIds]
 * @property {() => void} [onSuccess]
 * @property {string} successUrl
 * @property {string} errorUrl
 * @property {(params: {form: import("react-hook-form").UseFormReturn<FormValues>, isLoading: boolean}) => import("react").ReactElement} children
 *
 * @param {Props} props
 */
export default function PaymentsCreateHandler({
  environmentId,
  customerId,
  invoices,
  initialInvoiceIds = [],
  onSuccess,
  successUrl,
  errorUrl,
  children,
}) {
  const intl = useIntl();

  /** @type {import("react").MutableRefObject<HTMLFormElement | null>} */
  const refFormCreditCard = useRef(null);

  const { configuration } = useConfiguration();

  const [creditCardSettings, setCreditCardSettings] = useState(
    /**
     * @type {CreditCardSettings | null}
     */ (null),
  );

  const paymentMethods = useMemo(() => {
    return Object.entries(
      getPaymentsMethodsForType({
        configuration,
        type: PAYMENTS_TYPES_VALUE_PAYMENT,
        onlyAccount: true,
      }),
    );
  }, [configuration]);

  /** @type {import("react-hook-form").UseFormReturn<FormValues>} */
  const form = useForm({
    defaultValues: getDefaultValues({
      environmentId,
      // @ts-ignore
      method: paymentMethods.length === 1 ? paymentMethods[0][0] : undefined,
      customerId,
      invoices: invoices.map((invoice) => ({
        invoice_id: initString(invoice.id),
        amount: initString(invoice.pending_due_amount),
        isSelected:
          initialInvoiceIds.length > 0 // si on doit faire une initialisation
            ? initialInvoiceIds.includes(invoice.id) // on vérifie si l'invoice est dans la liste
              ? true
              : false
            : true, // sinon on sélectionne tout par défaut
      })),
    }),
  });
  const { handleSubmit } = form;

  const { request, isLoading, toastSuccess } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  /**
   * @param {FormValues} values
   */
  function onSubmit(values) {
    if (values.data.method === PAYMENTS_METHODS_VALUE_CREDIT_CARD_PAYBOX) {
      request(
        generateApiUri({
          id: "@payments.creditCardSettings",
          query: {
            invoices: values.data.invoices
              .filter((invoiceData) => invoiceData.isSelected)
              .map((invoiceData) => invoiceData.invoice_id),
            success_url: successUrl,
            error_url: errorUrl,
          },
        }),
      )
        .then((response) => {
          resetApiErrors();
          // React 18 defers this update: the form must be rendered before submit()
          flushSync(() => {
            // the reponse is not formatted properly
            // @ts-ignore
            setCreditCardSettings(response);
          });
          refFormCreditCard.current?.submit();
        })
        .catch((response) => {
          resolveApiError({ response, form });
        });
    } else {
      request(
        generateApiUri({
          id: "@payments.create",
        }),
        {
          method: "POST",
          body: normalizePaymentCreate(values),
        },
      )
        .then((response) => {
          toastSuccess({
            title: intl.formatMessage({
              defaultMessage: "Le paiement a bien été pris en compte.",
            }),
          });
          resetApiErrors();
          onSuccess?.();
        })
        .catch((response) => {
          resolveApiError({ response, form });
        });
    }
  }

  return (
    <>
      <FormProvider {...form}>
        <form id={PAYMENTS_CREATE_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          {children({ form, isLoading })}
        </form>
      </FormProvider>

      {creditCardSettings && (
        <form
          ref={refFormCreditCard}
          action={creditCardSettings.settings.url}
          method="post">
          {Object.entries(creditCardSettings.inputs).map(([name, value]) => [
            <input type="hidden" name={name} value={value} key={name} />,
          ])}
        </form>
      )}
    </>
  );
}
